import React, { createContext, useContext, ReactNode, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PaywallConfig } from '@/types/paywall';
import { usePaywall } from '@/hooks/usePaywall';
import { PaywallModal } from '@/components/PaywallModal';
import { setGlobalPaywallHandler, clearGlobalPaywallHandler } from '@/utils/paywall-interceptor';

interface PaywallContextType {
  isOpen: boolean;
  config: PaywallConfig | null;
  onBack?: () => void;
  openPaywall: (config: PaywallConfig, onBack?: () => void) => void;
  closePaywall: () => void;
  clearPaywall: () => void;
}

const PaywallContext = createContext<PaywallContextType | undefined>(undefined);

export const usePaywallContext = () => {
  const context = useContext(PaywallContext);
  if (!context) {
    throw new Error('usePaywallContext must be used within a PaywallProvider');
  }
  return context;
};

interface PaywallProviderProps {
  children: ReactNode;
}

export const PaywallProvider: React.FC<PaywallProviderProps> = ({ children }) => {
  const paywall = usePaywall();
  const navigate = useNavigate();

  // Set up global paywall handler with navigation context
  useEffect(() => {
    const handlePaywall = (config: PaywallConfig) => {
      // Check if we're in a content page context (fanfic, chapter, etc.)
      const currentPath = window.location.pathname;
      const isContentPage = currentPath.match(/\/(fanfic|chapter|story)\/\d+/);
      
      if (isContentPage) {
        // Provide navigation back functionality for content pages
        paywall.openPaywall(config, () => navigate(-1));
      } else {
        // Default behavior for other contexts
        paywall.openPaywall(config);
      }
    };
    
    setGlobalPaywallHandler(handlePaywall);
    return () => clearGlobalPaywallHandler();
  }, [paywall.openPaywall, navigate]);

  return (
    <PaywallContext.Provider value={paywall}>
      {children}
      {paywall.config && (
        <PaywallModal
          isOpen={paywall.isOpen}
          onClose={paywall.closePaywall}
          onBack={paywall.onBack}
          config={paywall.config}
        />
      )}
    </PaywallContext.Provider>
  );
};